'use client'
import getLocale from '@/stores/locales'
import NextLinkWrapper from '@/utils/NextLink'
import {
  beaconAttribute,
  hasOnBasketChangeBeaconAttribute,
  hasOnClickBeaconAttribute,
  hasSponsored,
} from '@/utils/hasSponsored'

import {
  useInteraction,
  useProductItemEvent,
} from '@mercadoni/sinister/dist/cjs/monitors/hooks' // TODO: review size of this component

import ProductCardFullLoad from '@mercadoni/sinister/dist/cjs/productv2ProductCardFullLoad' // TODO: review size of this component
import { getProductListInfo } from '@mercadoni/sinister/dist/cjs/productv2Utils'

import { UseLoggedContext } from '@mercadoni/sinister/dist/cjs/authentication'
import { trackEvent } from '@mercadoni/sinister/dist/cjs/monitors/services'
import { useHeaderDataContext } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import { UseListContext } from '@mercadoni/sinister/dist/cjs/shoppingLists'
import { ClientModel } from '@mercadoni/sinister/dist/lib/src/entities/clientModel'

import { usePathname, useSearchParams } from 'next/navigation'

import { ReactProfiler } from '@/components/ReactProfiler'
import { useReplacementsContext } from '@/context/ReplacementsContext'
import { useRestrictedAgeContext } from '@/context/RestrictedAgeContext'
import useGuessAction from '@/hooks/useGuessAction'
import goToProduct from '@/navigation/CSR/goToProduct'
import gotToPromoTag from '@/navigation/CSR/gotToPromoTag'
import { useRouter } from 'next/navigation'
import { ProductCatalog } from '~types/index'

const ProductWrapper = ({
  product,
  index,
  clientConfig,
}: {
  product: ProductCatalog
  index: number
  clientConfig: ClientModel
}) => {
  const trackSource = 'search'

  const { onAddProduct } = useReplacementsContext()
  const { sendInteractionEvent } = useInteraction()
  const { sendSelectProductEvent } = useProductItemEvent()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const copies = getLocale()

  const { favoriteService } = UseListContext()
  const { headerData, filtersPathInfo, pathNSearch } = useHeaderDataContext()
  const { onBeforeAddProduct } = useRestrictedAgeContext()
  const { isLogged } = UseLoggedContext()
  const listInfo = {
    screen_name: headerData?.screenName,
    filters: filtersPathInfo,
    keyword: filtersPathInfo?.searchTerm,
    path: pathNSearch?.pathname,
  }

  const buildListInfo = (product: ProductCatalog) => {
    return {
      ...listInfo,
      ...getProductListInfo(
        trackSource,
        product?.categories?.[0]?.name,
        product?.categories?.[0]?.reference
      ),
    }
  }

  const onClickPromoTag = gotToPromoTag(router)
  const onGuessAction = useGuessAction({ router, forceToLogin: false })

  const onProductClick = (product: ProductCatalog) => {
    sendSelectProductEvent({
      trackSource: 'search',
      product,
    })
  }

  const onClick = (product: ProductCatalog) => () => {
    if (hasSponsored({ clientConfig }) && hasOnClickBeaconAttribute(product)) {
      trackEvent({
        path: pathname,
        type: 'product',
        eventName: 'triggerOnClickBeacon',
        data: product?.promoted?.onClickBeacon,
      })
    }
    onProductClick(product)
  }

  const onClickAddProduct = (product: ProductCatalog) => {
    onAddProduct({ product })
  }

  const onInteraction =
    (product: ProductCatalog) => (interaction: { interactionName: string }) => {
      if (
        hasSponsored({ clientConfig }) &&
        interaction.interactionName === 'Agregar' &&
        hasOnBasketChangeBeaconAttribute(product)
      ) {
        trackEvent({
          path: pathname,
          type: 'product',
          eventName: 'triggerOnBasketChangeBeacon',
          data: product?.promoted?.onBasketChangeBeacon,
        })
      }
      sendInteractionEvent(interaction)

      if (clientConfig?.product?.variantsV2?.enabled) {
        const productSlug = product.slug
        goToProduct(router, productSlug, searchParams)
      }
    }

  return (
    <div
      key={product.sku}
      {...beaconAttribute(product)}
      style={{ height: '100%' }}
    >
      <ReactProfiler id="ProductWrapper">
        <ProductCardFullLoad
          index={index}
          product={product}
          isLogged={isLogged}
          copies={copies}
          trackSource={trackSource}
          onClick={onClick}
          clientConfig={clientConfig}
          favoriteService={favoriteService}
          onBeforeAddProduct={onBeforeAddProduct}
          onInteraction={onInteraction(product)}
          onCarlessProduct={onGuessAction}
          listInfo={buildListInfo(product)}
          onClickAddProduct={onClickAddProduct}
          onClickPromoTag={onClickPromoTag}
          LinkComponent={NextLinkWrapper}
        />
      </ReactProfiler>
    </div>
  )
}

export default ProductWrapper
